<template>
  <div
    v-if="siteIsLoadedAndEnabled"
    id="app"
    :style="`font-family: '${font}', Helvetica, Arial, sans-serif`"
  >
    <template v-if="siteIsLoadedAndEnabled">
      <a
        ref="skipToContent"
        href="#container"
        class="skip-to-content"
      >
        {{ componentTranslations.globals.skip_to_content }}
      </a>
      <AdvancedSearch
        v-if="isSearchOpen"
        :translations="componentTranslations.globals"
      />
      <Lightbox
        v-if="showLightbox"
        :translations="componentTranslations.globals"
      />
      <div
        v-if="hasGlobalContainers"
        class="global-containers"
      >
        <ViewContainer
          v-for="container in globalContainers"
          :key="container.containerIndex"
          :container="container"
          :container-idx="container.containerIndex"
        />
      </div>
    </template>
    <header id="view-header">
      <ClientOnly>
        <AlertContainer
          :hide-alerts="hideAlerts"
          @keyboard-close="focusTopElement"
        />
      </ClientOnly>
      <div
        v-if="isIntranet && $auth?.value?.loggedIn"
        class="intranet-header"
      >
        <div>
          <h2>{{ $t('intranet.navHeading', { orgName: primaryOrgName }) }}</h2>
          <div class="auth-info">
            <span
              >{{ $t('intranet.userHeading') }}<strong>&nbsp;{{ $auth?.value?.user?.profile?.name }}</strong></span
            >
            <TSAvatar
              size="small"
              height="30px"
              :src="$auth?.value?.user?.profile?.avatar"
            />
            <a
              class="logout"
              href="#"
              @click="logout"
              >{{ $t('intranet.logout') }}</a
            >
          </div>
        </div>
      </div>
      <cms-header
        ref="cmsHeaderRef"
        :theme="headerOptions"
        :content="content"
        :current-domain="domain"
        :is-homepage="isHomepage"
        :media-subdomain="getNavigationSubdomain"
        :initial-is-mobile="initialIsMobile"
        @language-change="languageChange"
        @toggle-mobile-menu="toggleMobileMenu"
        @toggle-menu="toggleMenu"
        @toggle-search="toggleSearch"
      />
      <cms-menu
        v-show="!mobileHeaderStatus"
        :theme="menuOptions"
        :content="content"
        :ai-chat-widget-ref="aiChatWidgetRef"
        :is-chat-drawer-open="isChatDrawerOpen"
        @close-menu="closeAndFocusMenuButton"
      />
    </header>
    <template v-if="siteIsLoadedAndEnabled">
      <div
        id="container"
        class="main"
        role="main"
      >
        <slot />
      </div>

      <ViewFooter />
      <StickyFooter v-if="displayStickyFooter" />
      <AiChatWidget
        v-if="streamApiKey"
        ref="aiChatWidgetRef"
      />
      <browser-warning
        v-if="showingBrowserNotice"
        @close="closeBrowserModal()"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useLightboxStore } from '@/stores/lightboxStore';
import * as cheerio from 'cheerio';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import isEmpty from 'lodash/isEmpty';
import {
  useCookie,
  useDevice,
  definePageMeta,
  navigateTo,
  useHead,
  useOidcClient,
  useRoute,
  useRuntimeConfig,
} from '#imports';

import { formatPageTitle } from '../helpers/strings';
import ViewFooter from '../components/home_page/footers/ViewFooter.vue';
import Lightbox from '../components/global/Lightbox.vue';
import AdvancedSearch from '../components/improved_search/AdvancedSearch.vue';
import AlertContainer from '../components/home_page/alert_banner/AlertContainer.vue';
import BrowserWarning from '../components/global/BrowserWarning.vue';
import StickyFooter from '../components/home_page/sticky_footer/ViewStickyFooter.vue';
import ViewContainer from '../components/home_page/ViewContainer.vue';
import TSAvatar from '../components/intranet/dscl/Avatar.vue';
import AiChatWidget from '../components/global/AiChatWidget.vue';
import { getContrastColor } from '../helpers/colors';
import { useDialogObserver } from '@/composables/useDialogObserver';
import { menuButtonFocus } from '@/composables/useMenuButtonFocus';
import { useAiChatWidget } from '@/composables/useAiChatWidget';
import { useHeaderCriticalCss } from '@/composables/useHeaderCriticalCss';

defineOptions({ name: 'App' });

const { isSearchOpen, toggleSearch } = useSearch();
const streamApiKey = useRuntimeConfig().public.streamApiKey;
const store = useStore();
const lightboxStore = useLightboxStore();
const { isMobile: initialIsMobile } = useDevice();
let script = [];
const styleTags = [];
const { newMobileHeaderActive, styleTags: headerStyleTags } = useHeaderCriticalCss(store);
styleTags.push(...headerStyleTags);
const linkTags = store.state.globals.favicon?.url
  ? [{ rel: 'icon', type: 'image/x-icon', href: store.state.globals.favicon.url }]
  : [];
const organizationClassName = kebabCase(store.state.globals.info.school_name);

linkTags.push(
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: true },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;700;900&family=Work+Sans:ital,wght@0,300;0,400;0,500;0,700;0,900;1,300;1,400;1,700&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,900&family=Roboto+Slab:wght@300;400;500;700;900&display=swap',
  },
  { rel: 'stylesheet', href: '/alias_font_faces.css' },
);

let noscript = [];
if (store.state.globals.settings?.embedCodes) {
  const {
    googleAnalytics,
    facebookPixel,
    googleTagManager: { head: googleTagManagerHead, body: googleTagManagerBody },
    customCodes,
  } = store.state.globals.settings.embedCodes;
  const scriptsArray = [
    { type: 'googleAnalytics', code: googleAnalytics },
    { type: 'facebookPixel', code: facebookPixel },
    { type: 'googleTagManagerHead', code: googleTagManagerHead },
    { type: 'googleTagManagerBody', code: googleTagManagerBody },
    ...customCodes.map((code) => ({ type: 'customCode', code })),
  ];
  const scriptTypesInsertedInFooter = ['googleTagManagerBody', 'customCode'];
  const scriptAttrsToMaintain = ['src', 'async', 'defer'];

  // Client Added Style Tags
  try {
    styleTags.push(
      ...scriptsArray
        .filter((scriptObj) => scriptObj.code)
        .flatMap((scriptObj) => {
          try {
            const $ = cheerio.load(scriptObj.code);
            return $('style')
              .toArray()
              .map((node) => ({
                type: 'text/css',
                innerHTML: $(node).html(),
              }));
          } catch (error) {
            console.error('Error parsing style tags:', error);
            return [];
          }
        }),
    );
  } catch (error) {
    console.error('Error processing style tags:', error);
  }

  // Client Added Script Tags
  script = scriptsArray
    .filter((scriptObj) => scriptObj.code)
    .reduce((array, scriptObj) => {
      try {
        const $ = cheerio.load(scriptObj.code);
        const scripts = $('script')
          .toArray()
          .map((scriptNode) => {
            const $scriptNode = $(scriptNode);
            const currentAttrs = $scriptNode.attr();
            const validAttrs = Object.keys(currentAttrs).reduce((acc, key) => {
              if (scriptAttrsToMaintain.includes(key) || key.startsWith('data-')) {
                acc[key] = currentAttrs[key] === '' && !key.startsWith('data-') ? key : currentAttrs[key];
              }
              return acc;
            }, {});

            return {
              ...validAttrs,
              type: 'text/javascript',
              body: scriptTypesInsertedInFooter.includes(scriptObj.type),
              innerHTML: $scriptNode.html(),
            };
          });
        return [...array, ...scripts];
      } catch (error) {
        console.error('Error parsing script tags:', error);
        return array;
      }
    }, []);

  // Client Added Noscript Tags
  noscript = scriptsArray
    .filter((scriptObj) => scriptObj.code)
    .reduce((array, scriptObj) => {
      try {
        const $ = cheerio.load(scriptObj.code);
        const noScripts = $('noscript')
          .toArray()
          .map((node) => ({ innerHTML: $(node).html(), body: true }));
        return [...array, ...noScripts];
      } catch (error) {
        console.error('Error parsing noscript tags:', error);
        return array;
      }
    }, []);
}
if (store.state.theme?.template_code) {
  const externalScripts = store.state.theme.template_code.script_codes
    .filter((s) => s)
    .map((scriptUrl) => ({
      type: 'text/javascript',
      body: true,
      src: scriptUrl,
    }));
  const externalStylesheets = store.state.theme.template_code.style_codes
    .filter((s) => s)
    .map((styleUrl) => ({
      rel: 'stylesheet',
      href: styleUrl,
    }));

  script.push(...externalScripts);
  linkTags.push(...externalStylesheets);

  if (store.state.theme.template_code.css_code) {
    styleTags.push({
      type: 'text/css',
      innerHTML: store.state.theme.template_code.css_code,
    });
  }
  if (store.state.theme.template_code.js_code) {
    script.push({
      type: 'text/javascript',
      body: true,
      defer: true,
      innerHTML: store.state.theme.template_code.js_code,
    });
  }
}

const globalTheme = () => {
  const fallbackColor = '#000';
  const primaryColor = get(store.state.globals, 'info.primary_color.hex', fallbackColor);
  const secondaryColor = get(store.state.globals, 'info.secondary_color.hex', fallbackColor);
  return [
    `--primary-color: ${primaryColor};`,
    ` --primary-color-contrast: ${getContrastColor(primaryColor)};`,
    `--secondary-color: ${secondaryColor};`,
    `--secondary-color-contrast: ${getContrastColor(secondaryColor)};`,
    `--link-color: ${get(store.state.globals, 'info.links_color.hex', fallbackColor)};`,
    `--links-color: ${get(store.state.globals, 'info.links_color.hex', fallbackColor)};`,
    `--heading: ${get(store.state.globals, 'info.cms_heading_color.hex', fallbackColor)};`,
    `--heading-color: ${get(store.state.globals, 'info.cms_heading_color.hex', fallbackColor)};`,
    `--page-builder-color: ${get(store.state.globals, 'info.page_builder_theme_color.hex', fallbackColor)};`,
    `--alert-text-color: ${get(store.state.globals, 'info.alert_text_color.hex', fallbackColor)};`,
    `--alert-background-color: ${get(store.state.globals, 'info.alert_bg_color.hex', fallbackColor)};`,
    `--alert-link-color: ${get(store.state.globals, 'info.alert_link_color.hex', fallbackColor)};`,
    `--alert-lightbox-shadow-color: ${get(store.state.globals, 'info.alert_lightbox_overlay.hex', fallbackColor)};`,
    `--font-family: '${store.state.font}', Helvetica, Arial, sans-serif`,
  ];
};

script.unshift({
  type: 'text/javascript',
  body: false,
  innerHTML: `window.clientWorkStateTemp = JSON.parse(${JSON.stringify(
    JSON.stringify(store.state),
  )}); window.clientWorkState = JSON.parse(${JSON.stringify(JSON.stringify(store.getters.getClientWorkState))})`,
});

const robotsIndexMeta = store.state.domain.includes('sites.thrillshare')
  ? { hid: 'robots', name: 'robots', content: 'noindex' }
  : {};
useHead({
  htmlAttrs: {
    lang: store.state.translation.locale,
  },
  titleTemplate: (titleChunk) => {
    if (!titleChunk) {
      return store.state.globals.info.school_name;
    }
    return formatPageTitle(store.state.globals, 'info.school_name', titleChunk);
  },
  meta: [
    // hid is used as unique identifier. Do not use `vmid` for it as it will not work
    { hid: 'description', name: 'description', content: store.state.globals.info.school_tag_line },
    { hid: 'og:type', property: 'og:type', content: 'website' },
    { hid: 'og:title', property: 'og:title', content: store.state.globals.info.school_name },
    {
      hid: 'og:description',
      property: 'og:description',
      content: store.state.globals.info.school_tag_line,
      tagPriority: 'critical',
    },
    { hid: 'og:image', property: 'og:image', content: store.state.globals.logo?.url, tagPriority: 'critical' },
    // Twitter Tags
    { hid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
    { hid: 'twitter:title', name: 'twitter:title', content: store.state.globals.info.school_name },
    { hid: 'twitter:description', name: 'twitter:description', content: store.state.globals.info.school_tag_line },
    {
      hid: 'twitter:image',
      name: 'twitter:image',
      content: store.state.globals.logo?.url,
      tagPriority: 'critical',
    },
    robotsIndexMeta,
  ],
  link: linkTags,
  style: styleTags,
  bodyAttrs: {
    class: computed(() =>
      lightboxStore.showLightbox || store.state.menuStatus
        ? `${organizationClassName} noscroll`
        : organizationClassName,
    ),
    style: computed(globalTheme),
  },
  script,
  noscript,
});

const { isIntranet } = useIntranet();

if (isIntranet.value) {
  // this triggers a nuxt warning in console, we should figure out where to put this.
  // this should only exist at a page level, not a layout level
  definePageMeta({
    middleware: ['auth', 'intranet-roles'],
  });
}

useDialogObserver();

const { isOpen: isChatDrawerOpen } = useAiChatWidget();

const showingBrowserNotice = ref(false);
const mobileHeaderStatus = ref(false);
const isClient = ref(false);
const skipToContent = ref(null);
const cmsHeaderRef = ref(null);
const aiChatWidgetRef = ref(null);
let viewHeaderObserver = null;
const route = useRoute();

const globals = computed(() => store.state.globals);
const homepage = computed(() => store.state.homepage);
const theme = computed(() => store.state.theme);
const font = computed(() => store.state.font);
const domain = computed(() => store.state.domain);
const organizations = computed(() => store.state.organizations);
const menuStatus = computed(() => store.state.menuStatus);
const pathPrefix = computed(() => store.state.pathPrefix);
const isThemePreview = computed(() => store.state.isThemePreview);
const showLightbox = computed(() => lightboxStore.showLightbox);
const menu = computed(() => store.state.menu);
const customMenuItems = computed(() => store.state.customMenuItems);
const configLoading = computed(() => store.state.configLoading);
const featureFlags = computed(() => store.state.featureFlags);
const getNavigationSubdomain = computed(() => store.getters.getNavigationSubdomain);
const language = computed(() => store.state.translation.language);
const languages = computed(() => store.state.translation.languages);
const locale = computed(() => store.state.translation.locale);
const componentTranslations = computed(() => store.state.translation.componentTranslations);

const primaryOrgName = computed(() => store.state.organizations.find((org) => org.primary)?.name || '');

const hasGalleryImages = computed(() => homepage.value?.gallery?.slides.length > 0);

const getHeaderValues = computed(() => {
  const headerValues = { ...store.getters['translation/getComponentTranslation']('header') };
  if (isThemePreview.value) {
    headerValues.menu_dropdown_title = theme.value.header.options.menu_field;
    headerValues.schools_dropdown_title = theme.value.header.options.schools_field;
    headerValues.services_dropdown_title = theme.value.header.options.services_field;
  }
  return headerValues;
});

const headerOptions = computed(() => ({
  ...theme.value.header.options,
  translations: { ...getHeaderValues.value },
}));

const menuOptions = computed(() => ({
  ...theme.value.menu.options,
  translations: store.getters['translation/getComponentTranslation']('menu'),
}));

const translationsMerged = computed(() => ({
  ...componentTranslations.value.globals,
  ...theme.value.translations,
}));

const utilities = computed(() => homepage.value?.header?.links || []);

const iconLinks = computed(() => homepage.value?.header?.icon_links || []);

const orgDropdownTitle = computed(() =>
  globals.value.info.organization_type === 'Municipality'
    ? getHeaderValues.value.services_dropdown_title || 'Services'
    : getHeaderValues.value.schools_dropdown_title,
);

const hasCustomSchoolsDropdown = computed(() => theme.value?.header?.options?.custom_schools_dropdown);

const orgMenuContent = computed(() => homepage.value?.schools_menu?.menu || []);

const isHomepage = computed(() =>
  ['index', 'alias-index', 'alias-preview-token-id', 'preview-token-id'].includes(route.name),
);

const displayStickyFooter = computed(() => {
  const hasActiveStickyFooter = theme.value.sticky_footer?.options?.active && !isEmpty(homepage.value.sticky_footer);
  const currentPageHasStickyFooter = isHomepage.value || !homepage.value.sticky_footer?.show_only_on_homepage;
  return hasActiveStickyFooter && currentPageHasStickyFooter;
});

const globalContainers = computed(() => {
  if (!theme.value?.containers) return [];
  const containersWithIndex = theme.value.containers.map((container, idx) => ({
    ...container,
    containerIndex: idx,
  }));
  return containersWithIndex.filter((container) => !!container.global);
});

const hasGlobalContainers = computed(() => !isEmpty(globalContainers.value));

const siteIsLoadedAndEnabled = computed(() => {
  const isDisabledOnCMSPublic = featureFlags.value.disable_cms_public;
  return !configLoading.value && (!isDisabledOnCMSPublic || isThemePreview.value);
});

const content = computed(() => ({
  customMenuItems: customMenuItems.value,
  globals: globals.value,
  hasCustomSchoolsMenu: hasCustomSchoolsDropdown.value,
  hasGalleryImages: hasGalleryImages.value,
  iconLinks: iconLinks.value,
  isEditing: false,
  isIFrame: false,
  isPreview: isThemePreview.value,
  isSearchOpen: isSearchOpen.value,
  langCode: locale.value,
  langText: language.value,
  languages: languages.value,
  menuOptions: menuOptions.value,
  newMobileHeader: newMobileHeaderActive.value,
  orgDropdownTitle: orgDropdownTitle.value,
  orgMenuContent: orgMenuContent.value,
  organizations: organizations.value,
  pathPrefix: pathPrefix.value,
  sitemapMenuItems: menu.value,
  showMenu: menuStatus.value,
  translations: translationsMerged.value,
  utilities: utilities.value,
}));

const hideAlerts = computed(() => ({
  banner: mobileHeaderStatus.value && menuStatus.value,
  modal: showingBrowserNotice.value,
}));

async function logout() {
  try {
    store.commit('setNeedsRefresh', true);
    const oidcClient = useOidcClient()();
    await oidcClient.removeUser();
    const authCookie = useCookie('auth-token');
    authCookie.value = undefined;
    navigateTo('/intranet/login');
  } catch {
    console.warn('error logging out');
  }
}

function toggleMenu(status) {
  store.commit('toggleMenu', status);
}

async function languageChange(lang) {
  const languageMatch = languages.value.find(({ code }) => code === lang.code);
  if (languageMatch.code !== locale.value) {
    await store.dispatch('translation/setLangCookies', { ...languageMatch, shouldReload: true });
  }
}

function closeAndFocusMenuButton() {
  store.commit('closeMenu');
  const headerRef = cmsHeaderRef.value?.$refs?.header;
  if (!headerRef) return;
  const buttonToFocus = menuButtonFocus({
    refs: headerRef,
    headerVersion: theme.value.header.options.version,
  });
  nextTick(() => {
    if (buttonToFocus) {
      buttonToFocus.focus();
    }
  });
}

function focusTopElement() {
  nextTick(() => {
    skipToContent.value?.focus();
  });
}

function closeBrowserModal() {
  showingBrowserNotice.value = false;
  window.sessionStorage.setItem('UNSUPPORTED_BROWSER_NOTICE', true);
}

function toggleMobileMenu(status) {
  toggleMenu(status);
  mobileHeaderStatus.value = status;
  const headerRef = cmsHeaderRef.value?.$refs?.header;
  if (!headerRef) return;
  const buttonToFocus = menuButtonFocus({
    refs: headerRef,
    headerVersion: theme.value.header.options.version,
  });
  nextTick(() => {
    if (buttonToFocus) {
      buttonToFocus.focus();
    }
  });
}

function fireLoadEvents() {
  if (isIntranet.value) return;
  const isDomReady = () => document.readyState === 'complete';
  const interval = setInterval(() => {
    if (isDomReady()) {
      clearInterval(interval);
      const event = new CustomEvent('appLoaded');
      window.dispatchEvent(event);
      document.dispatchEvent(event);
    }
  }, 50);
}

onMounted(async () => {
  isClient.value = true;
  let isIE = false;
  const ie = window.navigator.userAgent.indexOf('MSIE ');
  const browserNotice = window.sessionStorage.getItem('UNSUPPORTED_BROWSER_NOTICE');
  if (ie > 0 || !!navigator.userAgent.match(/Trident.*rv:11\./)) {
    isIE = true;
  }
  if (isIE && !browserNotice) {
    showingBrowserNotice.value = true;
  }
  fireLoadEvents();

  if (newMobileHeaderActive.value && theme.value?.header?.options?.version !== 'H4') {
    await nextTick();
    const viewHeader = document.getElementById('view-header');
    const headerEl = cmsHeaderRef.value?.$el;
    if (viewHeader && headerEl) {
      viewHeaderObserver = new ResizeObserver(() => {
        viewHeader.style.minHeight = `${headerEl.offsetHeight}px`;
      });
      viewHeaderObserver.observe(headerEl);
    }
  }
});

onBeforeUnmount(() => {
  viewHeaderObserver?.disconnect();
});
</script>

<style lang="scss">
@use '../components/global/scss/mixins/breakpoints';
html,
body,
#app {
  margin: 0;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  font-weight: 400;
  @media (prefers-reduced-motion: no-preference) {
    scroll-behavior: smooth;
  }
  &.no-scroll {
    height: 100%;
    overflow: hidden;
  }
}

/* iOS Safari auto-zooms on focus when font-size < 16px */
@supports (-webkit-touch-callout: none) {
  input,
  textarea,
  select {
    font-size: 16px !important;
    // since the according to design we need to have 14px font size, we need to scale the input down to 14px
    // it's a workaround to avoid the auto-zooming on focus and have the same size as the design
    transform: scale(0.875);
    transform-origin: top left;
  }
}

h1 {
  font-weight: 700;
  &.bold {
    font-weight: 900;
  }
}
h2,
h3,
h4,
h5 {
  font-weight: 700;
}
h1,
h2,
h3,
h4,
h5,
h6,
p {
  margin: 0;
}

/*Text align for News articles*/
.text-center {
  text-align: center;
}
.text-left {
  text-align: left;
}
.text-right {
  text-align: right;
}
.text-justify {
  text-align: justify;
}
// ensure main container is higher than footer
.main#container {
  position: relative;
  z-index: 1;
}

button.button-action {
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  padding: 0;
  .button-action-content {
    display: block;
  }
  &,
  > .button-action-content {
    outline: none;
    text-align: left;
    white-space: nowrap;
  }
  &:focus-visible > .button-action-content {
    outline: 1px dotted #212121;
    outline: 5px auto -webkit-focus-ring-color;
  }
}
.noscroll {
  overflow: hidden;
}
.page-container {
  width: 90%;
  margin: 0 auto;
  text-align: left;
  padding-top: 30px;
  padding-bottom: 70px;
  &:last-child {
    margin-bottom: 80px;
  }
  .filter {
    width: auto;
    float: right;
    select {
      line-height: 22px;
      margin-top: 10px;
      margin-bottom: 20px;
      width: 250px;
      padding: 5px 8px;
      padding-right: 33px;
      border: none;
      appearance: none;
      background: url('../assets/small_arrow_down.png') no-repeat 100% 50%;
      font-size: 16px;
      text-transform: uppercase;
      color: #717171;
    }
  }
  .page-container-item {
    border-bottom: 1px solid #ddd;
    padding: 18px 0;
    margin: 0 auto;
    overflow: hidden;
    clear: both;
  }
}
.search-enter-from,
.search-leave-to {
  transform: scale(0.9);
  opacity: 0;
}
.search-enter-to,
.search-leave-from {
  transform: scale(1);
  opacity: 1;
}
th {
  font-weight: 700;
}
blockquote,
strong {
  font-weight: 700;
}
.skip-to-content {
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  &:focus {
    width: auto;
    height: auto;
    background-color: #f1f1f1;
    border-radius: 3px;
    clip: auto;
    clip-path: none;
    color: #21759b;
    font-size: 14px;
    font-weight: bold;
    left: 5px;
    padding: 15px 23px 14px;
    top: 5px;
    text-align: center;
    z-index: 20;
  }
}

// Allows Google recaptcha privacy info to appear on top of modal
.grecaptcha-badge {
  z-index: 100;
}

.intranet-header {
  --big-screen-padding: 30px;
  --little-screen-padding: 10px;
  --intranet-nav-height-big: 60px;
  --intranet-nav-height-small: 45px;

  height: var(--intranet-nav-height-big);
  > div {
    color: white;
    position: fixed;
    z-index: 4;
    width: 100%;
    height: var(--intranet-nav-height-big);
    background-color: black;
    display: flex;
    align-items: center;
    justify-content: space-between;
    h2 {
      font-size: 16px;
      padding-left: var(--big-screen-padding);
    }
    .auth-info {
      display: flex;
      align-items: center;
      gap: 6px;
      padding-right: var(--big-screen-padding);
      .logout {
        color: #02a0e3;
      }
    }
  }
  @include breakpoints.screen('<=sm') {
    height: var(--intranet-nav-height-small);
    div {
      height: var(--intranet-nav-height-small);
      h2 {
        display: none;
      }
      .auth-info {
        padding-left: var(--little-screen-padding);
      }
    }
  }
}
</style>
